import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { clsx } from 'clsx';

import { formatter } from '@autopass-web/domain';
import { MochiInput } from '@autopass-web/mochi-component';

import type { MochiInputProps } from '@autopass-web/mochi-component';

interface CreditCardInputFormProps {
  variant?: MochiInputProps['variant'];
  onCardNumberFocus?: () => void;
  onExpiredDateFocus?: () => void;
  onCvcFocus?: () => void;
}

export const CreditCardInputForm = ({
  variant = 'normal',
  onCardNumberFocus,
  onExpiredDateFocus,
  onCvcFocus,
}: CreditCardInputFormProps) => {
  const {
    register,
    setValue,
    setError,
    formState: { errors },
  } = useFormContext<{
    creditCard: {
      number: string;
      exp: string;
      cvc: string;
    };
  }>();
  const creditCardNumberRef = useRef<HTMLInputElement | null>(null);
  const expiredDateRef = useRef<HTMLInputElement | null>(null);
  const cvcRef = useRef<HTMLInputElement | null>();

  const { ref: numberRefFromRHF, ...numberRestProps } =
    register('creditCard.number');
  const { ref: expiredDateRefFromRHF, ...expiredDateRestProps } =
    register('creditCard.exp');
  const { ref: cvcRefFromRHF, ...cvcRestProps } = register('creditCard.cvc');

  return (
    <div
      className={clsx('flex', 'flex-col', 'items-stretch', 'w-full', 'gap-2')}
    >
      <MochiInput
        autoComplete="cc-number"
        maxLength={19}
        variant={variant}
        onFocus={onCardNumberFocus}
        placeholder="信用卡/金融卡號碼"
        {...numberRestProps}
        errorMessage={errors['creditCard']?.number?.message}
        ref={(node) => {
          numberRefFromRHF(node);
          creditCardNumberRef.current = node;
        }}
        onChange={(e) => {
          const value = e.target.value;
          const formattedValue = formatter(value, ' ', 4);
          setValue('creditCard.number', formattedValue);
          setError('creditCard.number', { message: undefined });

          if (value.length === 19) {
            expiredDateRef.current?.focus?.();
          }
        }}
      />
      <div className={clsx('flex', 'gap-2', 'items-start')}>
        <MochiInput
          className={clsx('w-full')}
          autoComplete="cc-exp"
          variant={variant}
          onFocus={onExpiredDateFocus}
          inputMode="numeric"
          maxLength={5}
          placeholder="有效日期 MM/YY"
          ref={(node) => {
            expiredDateRefFromRHF(node);
            expiredDateRef.current = node;
          }}
          {...expiredDateRestProps}
          errorMessage={errors['creditCard']?.exp?.message}
          onChange={(e) => {
            const value = e.target.value;
            const formattedValue = formatter(value, '/', 2);
            setValue('creditCard.exp', formattedValue);
            setError('creditCard.exp', { message: undefined });

            if (value.length === 5) {
              cvcRef.current?.focus?.();
            }
          }}
        />
        <MochiInput
          className={clsx('w-36')}
          autoComplete="cc-csc"
          variant={variant}
          onFocus={onCvcFocus}
          inputMode="numeric"
          maxLength={3}
          placeholder="安全碼"
          ref={(node) => {
            cvcRefFromRHF(node);
            cvcRef.current = node;
          }}
          {...cvcRestProps}
          onChange={(e) => {
            cvcRestProps.onChange(e);

            setError('creditCard.cvc', { message: undefined });
          }}
          errorMessage={errors['creditCard']?.cvc?.message}
        />
      </div>
    </div>
  );
};
